import { Link } from 'react-router-dom';
import CaseStudyPageNav from '../../../components/feature/CaseStudyPageNav';
import CaseStudyNav from '../../../components/feature/CaseStudyNav';
import { useSEO } from '../../../hooks/useSEO';
import JsonLd from '../../../components/feature/JsonLd';

export default function ChiesiPatientExperienceFrameworkPage() {
  useSEO({
    title: 'Chiesi Patient Experience Framework | Design For',
    description: 'Creating a comprehensive patient experience framework for pharmaceutical clinical trials. Strategic framework for patient-centric healthcare delivery.',
    keywords: 'patient experience framework, healthcare strategy, clinical trials, pharmaceutical consulting, patient-centric care',
    canonical: '/work/chiesi-patient-experience-framework',
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/ee9b8a579b0f0dfe46dd47075426190b.jpeg',
  });

  const jsonLdData = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: 'Creating a comprehensive patient experience framework',
    description: 'We developed a strategic patient experience framework for Chiesi that guides clinical trial design, patient engagement, and healthcare delivery across their respiratory disease programmes.',
    url: 'https://example.com/work/chiesi-patient-experience-framework',
    author: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Design For',
      url: 'https://example.com',
    },
    about: 'Global pharmaceutical company - Patient experience strategy',
    keywords: 'patient experience framework, healthcare strategy, clinical trials, pharmaceutical consulting, patient-centric care',
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={jsonLdData} />
      <CaseStudyPageNav />

      {/* Main Content */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Link to="/work" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-10">
            <i className="ri-arrow-left-line"></i>
            Back to Work
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Left: Text + Outcomes */}
            <div>
              <h1 className="text-4xl lg:text-5xl font-black text-black tracking-tight leading-tight mb-3">
                Creating a patient experience framework to set the foundations for change
              </h1>
              <h2 className="text-lg font-medium text-primary mb-8">Global pharmaceutical company</h2>

              <p className="text-base text-gray-600 leading-relaxed mb-4">
                The R&D organisation needed a clear, practical way to move from intent to action on patient centricity. With increasing competition for trial participation and declining recruitment and retention, the business needed a shared vision, clear priorities, and a scalable operating model.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-10">
                Working across functions in a highly regulated environment, we co-created a patient experience framework that aligned strategy, capability, governance, and delivery - ensuring patient engagement could be embedded into everyday R&D decision-making.
              </p>

              <h3 className="text-xl font-black text-black mb-5">Outcomes</h3>
              <ul className="space-y-3">
                {[
                  'Designed a Patient Experience Framework across five lenses: metrics, processes, enablers, roles, and capability',
                  'Co-created a clear vision, strategic goals, and success measures for patient engagement in R&D',
                  'Developed a prioritised roadmap and future state operating model',
                  'Produced a Patient Experience Playbook for clinical trial teams',
                  'Built capability through collaborative workshops delivered alongside the work',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-base text-gray-700 leading-relaxed">
                    <span className="mt-2 w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: Images */}
            <div className="flex flex-col gap-6">
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/e7148a0e3de63bc88e3e7e813263df57.jpeg"
                  alt="Chiesi patient experience framework"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
              <div className="bg-gray-50 rounded-2xl flex items-center justify-center p-6 min-h-[340px]">
                <img
                  src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/dbb64eba24d9fc04f266df2e7d9b6e3b.jpeg"
                  alt="Patient journey mapping workshop materials"
                  className="max-w-full max-h-[420px] object-contain"
                />
              </div>
            </div>
          </div>

          {/* Footnote */}
          <p className="mt-12 text-xs text-gray-400 italic">
            This work was delivered leading a consulting team during a previous role.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <CaseStudyNav currentSlug="chiesi-patient-experience-framework" />

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png"
                alt="Design For"
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</a>
              <a href="#" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
